import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { errorMessage, useCompleteSalesforceInstall } from '../../../../api/integrations.js';
import { LoadingArea, StartPageLink, TextPage } from '../../../../components/PlaceholderPage.js';

/**
 * `/integrations/salesforce/callback`: where Salesforce sends the browser back
 * after "Connect Salesforce" (`features/integrations`).
 */
const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/_app/integrations/salesforce/callback')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SalesforceCallbackPage,
});

const TITLE = "Salesforce wasn't connected";

function SalesforceCallbackPage() {
  const { code, state, error } = Route.useSearch();
  const navigate = useNavigate();
  const complete = useCompleteSalesforceInstall();
  const sent = useRef(false);

  const canComplete = error === undefined && code !== undefined && state !== undefined;

  useEffect(() => {
    if (!canComplete || sent.current) return;
    sent.current = true;
    complete.mutate(
      { code, state },
      {
        onSuccess: ({ websiteId }) =>
          void navigate({
            to: '/websites/$websiteId/integrations',
            params: { websiteId },
            search: { integration: 'salesforce' },
            replace: true,
          }),
      },
    );
  }, [canComplete, code, state, complete, navigate]);

  if (error !== undefined) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {error === 'access_denied'
          ? 'Groundly wasn’t allowed to access Salesforce. Connect Salesforce again from the website’s Integrations when you’re ready.'
          : 'Salesforce stopped before the connection was made. Connect Salesforce again from the website’s Integrations.'}
      </TextPage>
    );
  }
  if (!canComplete) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        This page is only for coming back from Salesforce. Connect Salesforce from the
        website&apos;s Integrations.
      </TextPage>
    );
  }
  if (complete.isError) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {errorMessage(
          complete.error,
          'Groundly couldn’t reach the server. Connect Salesforce again.',
        )}
      </TextPage>
    );
  }
  return <LoadingArea />;
}
